import React, { useRef, useCallback, useEffect, useState } from 'react'
import { Box, Grid, Typography } from '@mui/material';
import { formStyle, stepNumberStyle, formTitleStyle, formDesStyle, topUnderlineStyle, sectionContainer, titleLabel, defaultLabel, fieldDataStyle } from '../../../constant/material_style';
import CustomTextfield from '../../../component/custom_textfield';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import { co_acceptance_type, co_witness_type, cr_acceptance_type, cr_witness_type } from '../../../constant/common_use';
import { useStateContext } from '../../../contexts/StepFormContextProvider';
import { handleDeleteAttachment, handleUpdateForm, uploadImageToServer } from '../../../api/rc_admission_form';
import { b64toBlob, base64ToImage, uploadImgToServer } from '../../../utils/helper/rc_form_helper';

const ServiceAgreementAcceptance = ({ currentStep, setCurrentStep, formValues, setFormValues, getSignature, handleUpdateSignature }) => {
	const { control, reset, handleSubmit, setValue, formState: { errors }} = useForm({ mode: 'onChange' });
	const { formId } = useStateContext();

	const [savedSignature, setSavedSignature] = useState({
		cr_acceptance_type: false,
		cr_witness_type: false,
		co_acceptance_type: false,
		co_witness_type: false
	});

	useEffect(() => {
		reset(formValues);
	}, [formValues.cr_name, formValues.cr_ic_passport, formValues.co_name, formValues.co_ic_passport]);

	useEffect(() => {
		setValue('cr_witness_name', getSignature(cr_witness_type)?.name);
		setValue('cr_witness_ic_passport', getSignature(cr_witness_type)?.ic_passport);
		setValue('cr_witness_date', getSignature(cr_witness_type)?.cr_witness_date);
		setValue('co_witness_name', getSignature(co_witness_type)?.name);
		setValue('co_witness_ic_passport', getSignature(co_witness_type)?.ic_passport);
		setValue('co_witness_date', getSignature(co_witness_type)?.cr_witness_date);
	}, [setValue, formValues]);

	const recipientRef = useRef({});
	const recipientWithness = useRef({});
	const ownerRef = useRef({});
	const ownerWithness = useRef({});

	const debounceSetValues = useCallback(
		_.debounce((name, value, type) => {
			setValue(name, value, { shouldValidate: true });

			setFormValues((prev) => {
				const newFormValues = { ...prev, [name]: value }
				const updatedValue = value.filter(val => val.type === type);

				if(formId) {
					handleUpdateForm(formId, {signatures: updatedValue});
				}

				setFormValues(newFormValues);
				return newFormValues;
			});
		}, 800), [setValue, setFormValues, formId]
	);

	const clearSignature = async (e, signatureRef, type) => {
		e.preventDefault();
		signatureRef.current.clear();

		const filteredData = formValues.signatures.filter((signature) => signature.type === type);

		if (filteredData) {
			const updatedSignatures = formValues.signatures.map((signature) => {
				if(signature.type === type) {
					if(signature.hasOwnProperty('id')) {
						delete signature.id;
					}

					signature.signature_url_s3 = '';
				}

				return signature;
			});

			if (filteredData[0].signature_url_s3.trim() !== '') {
				const deleteSig = {...filteredData[0]};
				delete deleteSig.ic_passport;
				delete deleteSig.name;
				delete deleteSig.type;
				deleteSig.delete = 1;

				await handleDeleteAttachment(formId, { signatures: [deleteSig] });
			}

			setFormValues({ ...formValues, signatures: updatedSignatures });
			await handleUpdateForm(formId, { signatures: filteredData });
		}
	};

	const saveSignature = async (e, canva, type, name, ic_passport) => {
		e.preventDefault();
		const url = canva.current.getTrimmedCanvas().toDataURL('image/png');
		const base64Data = url.replace(/^data:image\/\w+;base64,/, '');

		const imgFile = base64ToImage(url, type);
		const imgBlob = b64toBlob(base64Data, 'image/png');

		const signatureAttachments = [];
		let imgObj = {
			prefix: "1/admission/documents",
			filename: imgFile.name,
		};

		signatureAttachments.push(imgObj);
		let transformData = { payload: signatureAttachments }
		const res = await uploadImageToServer(transformData);

		const convertedAttachments = [];
		if(res) {
			const uploadUrl = Object.keys(res).map(key => {
				const attachmentArr = res[key];
				return attachmentArr[0].uploadUrl;
			}).pop();

			await uploadImgToServer(uploadUrl, imgBlob);

			Object.keys(res).forEach(key => {
				const attachmentArr = res[key];

				attachmentArr.forEach(attachment => {
					const convertedFiles = {
						attachment_url_s3: attachment?.downloadUrl,
						attachment_name: attachment?.filePath.split('/').pop(),
					}

					convertedAttachments.push(convertedFiles);
				});
			})
		}

		const result = handleUpdateSignature(type, 'signature_url_s3', convertedAttachments[0].attachment_url_s3, name, ic_passport);
		if(result) {
			setSavedSignature(prevState => ({
				...prevState,
				[type]: true
			}));
		}
	};

	const handleChange = (type, objKey, val) => {
		const updatedSignatures = formValues.signatures.map((signature) => {
			return signature.type === type ? {
				...signature, [objKey]: val,
			} : signature
		});

		debounceSetValues('signatures', updatedSignatures, type);
	}

	const goback = (e) => {
		setCurrentStep(currentStep - 1);
	}

	const onSubmit = (e) => {
		setCurrentStep(currentStep + 1);
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<Box style={formStyle}>
				<Grid container alignItems="flex-start" spacing={2} style={{ marginBottom: '20px', overflowY: 'auto' }}>
					<Grid item>
						<Box style={stepNumberStyle}>{currentStep}</Box>
					</Grid>
					<Grid item xs style={{ paddingLeft: '0' }}>
						<Typography style={formTitleStyle}>Service Agreement - Acceptance</Typography>
					</Grid>
				</Grid>

				{/* Resident section */}
				<>
					<Box style={sectionContainer}>
						<Box style={topUnderlineStyle}>
							<Typography style={formDesStyle}>Resident</Typography>
						</Box>
					</Box>

					<Box style={sectionContainer}>
						<Typography style={defaultLabel}>
							I, <span style={fieldDataStyle}>{formValues.cr_name}</span> (NRIC No. <span style={fieldDataStyle}>{formValues.cr_ic_passport}</span>) of <span style={fieldDataStyle}>{formValues.cr_address}</span> hereby acknowledge and confirm that I/We read and understood all the terms and conditions hereinabove. I/We hereby agree and accept the terms and conditions hereinabove.
						</Typography>
					</Box>
					<div style={{ marginBottom: '60px' }}></div>
					<Box style={sectionContainer}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<CustomTextfield
									name={getSignature(cr_acceptance_type)} formValue={getSignature(cr_acceptance_type)?.signature_url_s3} label="Signature" typeOfField="signature" sigCanvas={recipientRef}
									saveSignature={(e) => saveSignature(e, recipientRef, cr_acceptance_type, formValues.cr_name, formValues.cr_ic_passport)} savedSignature={savedSignature.cr_acceptance_type}
									clearSignature={(e) => clearSignature(e, recipientRef, cr_acceptance_type)}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<CustomTextfield name='cr_name' formValue={formValues.cr_name} label="Full Name" typeOfField="text1" readOnly={true} control={control} errors={errors} />
								<CustomTextfield name='cr_ic_passport' formValue={formValues.cr_ic_passport} label="IC / Passport" typeOfField="text1" readOnly={true} control={control} errors={errors} />
								<CustomTextfield name='cr_acceptance_date' formValue={getSignature(cr_acceptance_type)?.date} label="Date" typeOfField="datePicker" onChangeVal={(name, val) => handleChange(cr_acceptance_type, 'date', val)} disableFuture={false} disablePast={true} />
							</Grid>
						</Grid>
					</Box>

					{/* Recipient Withness */}
					<div style={{ marginTop: '60px' }}></div>
					<Box style={sectionContainer}>
						<Typography style={titleLabel}>Witness By:-</Typography>
					</Box>
					<Box style={sectionContainer}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<CustomTextfield
									name={getSignature(cr_witness_type)} formValue={getSignature(cr_witness_type)?.signature_url_s3} label="Signature" typeOfField="signature" sigCanvas={recipientWithness}
									saveSignature={(e) => saveSignature(e, recipientWithness, cr_witness_type)} savedSignature={savedSignature.cr_witness_type}
									clearSignature={(e) => clearSignature(e, recipientWithness, cr_witness_type)}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<CustomTextfield name='cr_witness_name' formValue={getSignature(cr_witness_type)?.name} label="Full Name" typeOfField="text1" onChangeVal={(name, val) => handleChange(cr_witness_type, 'name', val)} control={control} errors={errors} />
								<CustomTextfield name='cr_witness_ic_passport' formValue={getSignature(cr_witness_type)?.ic_passport} label="IC / Passport / Staff ID" typeOfField="text1" onChangeVal={(name, val) => handleChange(cr_witness_type, 'ic_passport', val)} control={control} errors={errors} />
								<CustomTextfield name='cr_witness_date' formValue={getSignature(cr_witness_type)?.date} label="Date" typeOfField="datePicker" onChangeVal={(name, val) => handleChange(cr_witness_type, 'date', val)} disableFuture={false} disablePast={true} />
							</Grid>
						</Grid>
					</Box>
				</>

				{/* Surety section */}
				<>
					<Box style={sectionContainer}>
						<Box style={topUnderlineStyle}>
							<Typography style={formDesStyle}>Surety</Typography>
						</Box>
					</Box>

					<Box style={sectionContainer}>
						<Typography style={defaultLabel}>
							I, <span style={fieldDataStyle}>{formValues.co_name}</span> (NRIC No. <span style={fieldDataStyle}>{formValues.co_ic_passport}</span>) of <span style={fieldDataStyle}>{formValues.co_address}</span> hereby acknowledge and confirm that I/We read and understood all the terms and conditions hereinabove. I/We hereby agree and accept the terms and conditions hereinabove.
						</Typography>
					</Box>
					<div style={{ marginBottom: '60px' }}></div>
					<Box style={sectionContainer}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<CustomTextfield
									name={getSignature(co_acceptance_type)} formValue={getSignature(co_acceptance_type)?.signature_url_s3} label="Signature" typeOfField="signature" sigCanvas={ownerRef}
									saveSignature={(e) => saveSignature(e, ownerRef, co_acceptance_type, formValues.co_name, formValues.co_ic_passport)} savedSignature={savedSignature.co_acceptance_type}
									clearSignature={(e) => clearSignature(e, ownerRef, co_acceptance_type)}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<CustomTextfield name='co_name' formValue={formValues.co_name} label="Full Name" typeOfField="text1" readOnly={true} control={control} errors={errors} />
								<CustomTextfield name='co_ic_passport' formValue={formValues.co_ic_passport} label="IC / Passport" typeOfField="text1" readOnly={true} control={control} errors={errors} />
								<CustomTextfield name='co_acceptance_date' formValue={getSignature(co_acceptance_type)?.date} label="Date" typeOfField="datePicker" onChangeVal={(name, val) => handleChange(co_acceptance_type, 'date', val)} disableFuture={false} disablePast={true} />
							</Grid>
						</Grid>
					</Box>

					{/* Owner Withness */}
					<div style={{ marginTop: '60px' }}></div>
					<Box style={sectionContainer}>
						<Typography style={titleLabel}>Witness By:-</Typography>
					</Box>
					<Box style={sectionContainer}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<CustomTextfield
									name={getSignature(co_witness_type)} formValue={getSignature(co_witness_type)?.signature_url_s3} label="Signature" typeOfField="signature" sigCanvas={ownerWithness}
									saveSignature={(e) => saveSignature(e, ownerWithness, co_witness_type)} savedSignature={savedSignature.co_witness_type}
									clearSignature={(e) => clearSignature(e, ownerWithness, co_witness_type)}
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<CustomTextfield name='co_witness_name' formValue={getSignature(co_witness_type)?.name} label="Full Name" typeOfField="text1" onChangeVal={(name, val) => handleChange(co_witness_type, 'name', val)} control={control} errors={errors}  />
								<CustomTextfield name='co_witness_ic_passport' formValue={getSignature(co_witness_type)?.ic_passport} label="IC / Passport / Staff ID" typeOfField="text1" onChangeVal={(name, val) => handleChange(co_witness_type, 'ic_passport', val)} control={control} errors={errors}  />
								<CustomTextfield name='co_witness_date' formValue={getSignature(co_witness_type)?.date} label="Date" typeOfField="datePicker" onChangeVal={(name, val) => handleChange(co_witness_type, 'date', val)} disableFuture={false} disablePast={true} />
							</Grid>
						</Grid>
					</Box>
				</>

				<div style={{ marginTop: '100px' }}></div>

				<div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '30px' }}>
					<button className='t-accept-button' onClick={goback}>Previous</button>
					<button type="submit" className='t-accept-button'>Next</button>
				</div>
			</Box>
		</form>
	);
}

export default ServiceAgreementAcceptance